import React, { useState, useRef, useEffect } from "react";
import { useLanguage } from '../StoreContext/LanguageContext';
import apiService from "../config";

const AdminCreateProject = ({ onBack }) => {
  const { language, translations } = useLanguage();
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [formData, setFormData] = useState({
    project_name: "",
    project_type: "",
    project_division: "",
    project_incharge_usr_id: "",
    project_category: "",
    document: null
  });
  const [documentFileName, setDocumentFileName] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [assignedRoles, setAssignedRoles] = useState([]);
  const dropdownRef = useRef(null);
  const [categories, setCategories] = useState([]);

  // ADD THESE NEW STATE VARIABLES:
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: '',
    message: '',
    type: 'success'
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    type: 'warning'
  });

  const projectTypes = [
    'Road Infrastructure',
    'Irrigation Infrastructure',
    'Urban Development',
    'Industrial Development',
    'Educational Infrastructure',
    'Healthcare Infrastructure',
    'Agricultural',
    'Transportation',
    'Building Construction'
  ];

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await apiService.getAllCategory();
        if (res?.status === 200 && res.data) {
          setCategories(res.data);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  // ADD THESE HELPER FUNCTIONS:
  const showAlert = (title, message, type = 'info') => {
    setAlertConfig({ title, message, type });
    setShowAlertModal(true);

    // Don't auto-hide success messages so user can see them
    if (type !== 'success') {
      setTimeout(() => setShowAlertModal(false), 5000);
    }
  };

  const showConfirm = (title, message, onConfirm, onCancel = null, type = 'warning') => {
    setConfirmConfig({ title, message, onConfirm, onCancel, type });
    setShowConfirmModal(true);
  };

  // Fetch assigned roles on component mount
  useEffect(() => {
    fetchAssignedRoles();
  }, []);

  const fetchAssignedRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await apiService.getAllUserProjectInchargeRole();
      if (response && response.data) {
        const formattedUsers = response.data.map(user => ({
          id: user.usr_id,
          usr_id: user.usr_id,
          username: user.full_name,
          mobileNo: user.mobile ? `+91 ${user.mobile}` : '',
          role: user.user_role,
          project_id: user.project_id,
          projectName: user.project_name,
          status: user.is_active
        }));
        setAssignedRoles(formattedUsers);
      }
    } catch (error) {
      console.error('Error fetching assigned roles:', error);
      showAlert('Error', 'Failed to load project managers', 'error');
    } finally {
      setLoadingRoles(false);
    }
  };

  // Add these helper functions after other helper functions
  const handleCheckboxChange = (userId) => {
    const selectedIds = Array.isArray(formData.project_incharge_usr_id)
      ? formData.project_incharge_usr_id
      : [];

    const newSelected = selectedIds.includes(userId)
      ? selectedIds.filter(id => id !== userId)
      : [...selectedIds, userId];

    setFormData({
      ...formData,
      project_incharge_usr_id: newSelected
    });
  };

  const handleRemoveUser = (userId) => {
    setFormData({
      ...formData,
      project_incharge_usr_id: formData.project_incharge_usr_id.filter(id => id !== userId)
    });
  };

  const getSelectedNames = () => {
    const selectedIds = Array.isArray(formData.project_incharge_usr_id)
      ? formData.project_incharge_usr_id
      : [];

    if (selectedIds.length === 0) return "Select Project Incharge";
    if (selectedIds.length === 1) {
      const user = assignedRoles.find(u => u.usr_id === selectedIds[0]);
      return user ? user.username : "1 selected";
    }
    return `${selectedIds.length} selected`;
  };

  const filteredRoles = assignedRoles.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.mobileNo.includes(searchTerm)
  );

  const selectedIds = Array.isArray(formData.project_incharge_usr_id)
    ? formData.project_incharge_usr_id
    : [];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      showAlert('File Too Large', 'Please select a file smaller than 10MB', 'warning');
      e.target.value = '';
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      showAlert('Invalid File Type', 'Please select a PDF, DOC/DOCX, or image file', 'warning');
      e.target.value = '';
      return;
    }

    setFormData({ ...formData, document: file });
    setDocumentFileName(file.name);
  };

  const handleRemoveDocument = () => {
    setFormData({ ...formData, document: null });
    setDocumentFileName("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.project_name || !formData.project_type || !formData.project_incharge_usr_id || !formData.project_category) {
      showAlert('Missing Information', 'Please fill in all required fields', 'warning');
      return;
    }

    setLoading(true);
    // setMessage({ type: '', text: '' });

    try {
      const result = await apiService.postAddProject({
        projectName: formData.project_name,
        projectType: formData.project_type,
        projectDivision: formData.project_division,
        projectInchargeUsrId: formData.project_incharge_usr_id,
        project_category: formData.project_category,
        document: formData.document
      });

      if (result.status === 200 || result.status === 201) {
        // ✅ STORE PROJECT CATEGORY
        localStorage.setItem(
          'project_category',
          JSON.stringify(formData.project_category)
        );

        showAlert('Success', 'Project created successfully!', 'success');

        setFormData({
          project_name: "",
          project_type: "",
          project_division: "",
          project_incharge_usr_id: [],
          project_category: "",
          document: null
        });
        setDocumentFileName("");
        window.location.href = '/dashboard';
      } else {
        throw new Error('Failed to create project');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      showAlert('Error', 'Failed to create project. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (typeof onBack === "function") {
      onBack();
    } else {
      setFormData({
        project_name: "",
        project_type: "",
        project_division: "",
        project_incharge_usr_id: "",
        project_category: "",
        document: null
      });
      setDocumentFileName("");
      setMessage({ type: '', text: '' });
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-8">
      {message.text && (
        <div className={`p-4 rounded-md mb-6 ${message.type === 'success'
          ? 'bg-green-100 border border-green-200 text-green-700'
          : 'bg-red-100 border border-red-200 text-red-700'
          }`}>
          {message.text}
        </div>
      )}

      <div className="space-y-6">
        {/* Project Name */}
        <div>
          <label className="block mb-2 text-gray-700 font-medium">
            {translations[language].ProjectName} *
          </label>
          <input
            type="text"
            name="project_name"
            value={formData.project_name}
            onChange={handleChange}
            placeholder={translations[language].EnterProjectName}
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        {/* Project Type */}
        <div>
          <label className="block mb-2 text-gray-700 font-medium">
            {translations[language].ProjectType} *
          </label>
          <select
            name="project_type"
            value={formData.project_type}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="">{translations[language].SelectProjectType}</option>
            {projectTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>

        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {translations[language].ProjectDivision} *
          </label>
          <select
            name="project_division"
            value={formData.project_division}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 `}
          >
            <option value="">{translations[language].SelectDivision}</option>
            <option value="State Government/Union Territories">{translations[language].StateGovUT}</option>
            <option value="Public Works Division">{translations[language].PublicWorksDivision}</option>
            <option value="Urban Development Division">{translations[language].UrbanDevDivision}</option>
            <option value="Rural Development Division">{translations[language].RuralDevDivision}</option>
            <option value="Industrial Development Division">{translations[language].IndustrialDevDivision}</option>
            {/* <option value="Ro">Ro</option> */}
          </select>
          {/* {validationErrors.project_division && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.project_division}</p>
              )} */}
        </div>

        {/* Project Incharge */}
        {/* <div>
          <label className="block mb-2 text-gray-700 font-medium">
            {translations[language].ProjectIncharge} *
          </label>
          <select
            name="project_incharge_usr_id"
            multiple
            value={formData.project_incharge_usr_id}
            onChange={(e) => {
              const selectedValues = Array.from(
                e.target.selectedOptions,
                option => option.value
              );
              setFormData({
                ...formData,
                project_incharge_usr_id: selectedValues
              });
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-md
             focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
            disabled={loadingRoles}
          >
            {assignedRoles.map((user) => (
              <option key={user.usr_id} value={user.usr_id}>
                {user.username}
              </option>
            ))}
          </select>

          {loadingRoles && (
            <p className="text-sm text-gray-500 mt-1">{translations[language].LoadingManagers}</p>
          )}
        </div> */}

        {/* Project Incharge */}
        <div className="relative">
          <label className="block mb-2 text-gray-700 font-medium">
            {translations[language].ProjectIncharge} *
          </label>

          {/* Dropdown Trigger */}
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            disabled={loadingRoles}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between hover:border-gray-400 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <span className={selectedIds.length === 0 ? "text-gray-400" : "text-gray-900 font-medium"}>
              {getSelectedNames()}
            </span>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && !loadingRoles && (
            <div className="relative" ref={dropdownRef}>
              {/* <div className="absolute z-20 w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-2xl overflow-hidden"> */}
              {/* Search Box */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-gray-200">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by name or mobile..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>

              {/* Options List */}
              <div className="overflow-y-auto max-h-72">
                {filteredRoles.length === 0 ? (
                  <div className="p-8 text-center">
                    <svg className="w-16 h-16 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p className="text-gray-500 font-medium">No users found</p>
                  </div>
                ) : (
                  filteredRoles.map((user) => (
                    <label
                      key={user.usr_id}
                      className={`flex items-center px-5 py-4 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 cursor-pointer transition-all border-b border-gray-100 ${selectedIds.includes(user.usr_id) ? 'bg-blue-50' : ''
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(user.usr_id)}
                        onChange={() => handleCheckboxChange(user.usr_id)}
                        className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      />

                      <div className="ml-4 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {user.username.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {user.username}
                            </div>
                            {user.mobileNo && (
                              <div className="text-xs text-gray-500 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                </svg>
                                {user.mobileNo}
                              </div>
                            )}
                          </div>
                        </div>
                        {user.role && (
                          <div className="mt-1 ml-12">
                            <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                              {user.role}
                            </span>
                          </div>
                        )}
                      </div>

                      {selectedIds.includes(user.usr_id) && (
                        <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </label>
                  ))
                )}
              </div>

              {/* Footer */}
              {selectedIds.length > 0 && (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-t-2 border-gray-200 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">
                    <span className="text-blue-600">{selectedIds.length}</span> user{selectedIds.length !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, project_incharge_usr_id: [] })}
                    className="text-sm text-red-600 hover:text-red-700 font-semibold hover:underline transition-all"
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>
          )}

          {loadingRoles && (
            <p className="text-sm text-gray-500 mt-1">
              {translations[language].LoadingManagers}
            </p>
          )}

          {/* Selected Pills */}
          {selectedIds.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {selectedIds.map(userId => {
                const user = assignedRoles.find(u => u.usr_id === userId);
                if (!user) return null;
                return (
                  <div
                    key={userId}
                    className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full text-sm font-medium shadow-md hover:shadow-lg transition-all"
                  >
                    <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center text-blue-600 font-bold text-xs mr-2">
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                    <span>{user.username}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveUser(userId)}
                      className="ml-2 hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-all"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Project Document */}
        <div>
          <label className="block mb-2 text-gray-700 font-medium">
            Project Document
          </label>

          {!documentFileName ? (
            <label className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
              <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-gray-600">Attach Document</span>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleDocumentChange}
                className="hidden"
              />
            </label>
          ) : (
            <div className="flex items-center justify-between w-full px-4 py-3 border border-gray-300 rounded-md bg-gray-50">
              <span className="text-sm text-gray-700 truncate">{documentFileName}</span>
              <button
                type="button"
                onClick={handleRemoveDocument}
                className="ml-3 text-red-500 hover:text-red-700 text-sm font-medium"
              >
                Remove
              </button>
            </div>
          )}
          <p className="text-sm text-gray-500 mt-1">PDF, DOC/DOCX or image, up to 10MB</p>
        </div>

        {/* Project Category */}
        <div>
          <label className="block mb-2 text-gray-700 font-medium">
            Project Category *
          </label>

          <select
            name="project_category"
            value={formData.project_category}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-md 
               focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">Select Project Category</option>
            {categories.map(cat => (
              <option key={cat.category_id} value={cat.eng_category_name}>
                {cat.category_name}
              </option>
            ))}
          </select>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-6">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200"
            disabled={loading}
          >
            {translations[language].Cancel}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
            disabled={loading || loadingRoles}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {translations[language].Creating}
              </>
            ) : (
              <span>{translations[language].CreateProject}</span>
            )}
          </button>
        </div>
      </div>

      {/* ADD THESE MODAL COMPONENTS: */}
      {/* Simple Alert Notification */}
      {showAlertModal && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`bg-white rounded-lg shadow-lg border-l-4 p-4 max-w-sm ${alertConfig.type === 'error' ? 'border-red-500' :
            alertConfig.type === 'warning' ? 'border-yellow-500' :
              alertConfig.type === 'info' ? 'border-blue-500' :
                'border-green-500'
            }`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${alertConfig.type === 'error' ? 'bg-red-100' :
                  alertConfig.type === 'warning' ? 'bg-yellow-100' :
                    alertConfig.type === 'info' ? 'bg-blue-100' :
                      'bg-green-100'
                  }`}>
                  <span className={`font-bold ${alertConfig.type === 'error' ? 'text-red-600' :
                    alertConfig.type === 'warning' ? 'text-yellow-600' :
                      alertConfig.type === 'info' ? 'text-blue-600' :
                        'text-green-600'
                    }`}>
                    {alertConfig.type === 'error' ? '✕' :
                      alertConfig.type === 'warning' ? '⚠' :
                        alertConfig.type === 'info' ? 'ℹ' : '✓'}
                  </span>
                </div>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-gray-900">{alertConfig.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{alertConfig.message}</p>
              </div>
              <button
                onClick={() => setShowAlertModal(false)}
                className="ml-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Simple Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm mx-4">
            <div className="p-6">
              <div className={`w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center ${confirmConfig.type === 'danger' ? 'bg-red-100' :
                confirmConfig.type === 'warning' ? 'bg-yellow-100' :
                  'bg-blue-100'
                }`}>
                <span className={`text-2xl ${confirmConfig.type === 'danger' ? 'text-red-600' :
                  confirmConfig.type === 'warning' ? 'text-yellow-600' :
                    'text-blue-600'
                  }`}>
                  {confirmConfig.type === 'danger' ? '⚠' :
                    confirmConfig.type === 'warning' ? '⚠' : '?'}
                </span>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                {confirmConfig.title}
              </h3>

              <p className="text-gray-600 text-sm mb-6 text-center">
                {confirmConfig.message}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    if (confirmConfig.onCancel) confirmConfig.onCancel();
                  }}
                  className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  <span>{translations[language].Cancel}</span>
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    if (confirmConfig.onConfirm) confirmConfig.onConfirm();
                  }}
                  className={`flex-1 py-2 px-4 text-white rounded-md transition-colors ${confirmConfig.type === 'danger' ? 'bg-red-600 hover:bg-red-700' :
                    confirmConfig.type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' :
                      'bg-blue-600 hover:bg-blue-700'
                    }`}
                >
                  <span>{translations[language].Confirm}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminCreateProject;